import React, { useState, useEffect } from "react";

const TrafficOverviewWidget = ({ 
  interval, 
  apiBaseUrl = "https://alb.api-dev-conquerlifeco.com/api/analytics" 
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTrafficOverview();
  }, [interval]);

  const fetchTrafficOverview = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${apiBaseUrl}/user_website/traffic_overview/${interval}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch traffic overview: ${response.status}`);
      }

      const result = await response.json();
      
      // The API returns an array with a single object containing the metrics
      if (result && result.length > 0) {
        const metrics = result[0];
        setData({
          totalVisits: metrics[0] || 0,
          uniqueVisitors: metrics[1] || 0,
          totalSessions: metrics[2] || 0,
          uniqueIps: metrics[3] || 0,
          newVisitors: metrics[4] || 0,
          botVisits: metrics[5] || 0,
          avgPageLoadTime: metrics[6] || 0,
        });
      } else {
        setData({
          totalVisits: 0,
          uniqueVisitors: 0,
          totalSessions: 0,
          uniqueIps: 0,
          newVisitors: 0,
          botVisits: 0,
          avgPageLoadTime: 0,
        });
      }
    } catch (err) {
      console.error("Error fetching traffic overview:", err);
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0";
    return num.toLocaleString();
  };

  const formatLoadTime = (ms) => {
    if (ms === null || ms === undefined) return "0ms";
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getStatColor = (index) => {
    const colors = [
      "#667eea", // Purple - Total Visits
      "#48bb78", // Green - Unique Visitors
      "#f59e0b", // Orange - Total Sessions
      "#06b6d4", // Cyan - Unique IPs
      "#ec4899", // Pink - New Visitors
      "#ef4444", // Red - Bot Visits
      "#ecc94b", // Yellow - Avg Load Time
    ];
    return colors[index] || "#94a3b8";
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          color: "#a0aec0",
          fontSize: "14px",
        }}
      >
        <div
          style={{
            width: "32px",
            height: "32px",
            border: "3px solid #e2e8f0",
            borderTopColor: "#667eea",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <span style={{ marginTop: "12px" }}>Loading traffic overview...</span>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          color: "#a0aec0",
          fontSize: "14px",
        }}
      >
        <span style={{ fontSize: "32px", marginBottom: "8px" }}>⚠️</span>
        <div style={{ color: "#2d3748", fontWeight: "600" }}>Failed to load traffic data</div>
        <small style={{ fontSize: "11px", color: "#cbd5e0", marginTop: "8px" }}>
          {error}
        </small>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          color: "#a0aec0",
          fontSize: "14px",
        }}
      >
        📊 No traffic data available
        <small style={{ fontSize: "11px", color: "#cbd5e0", marginTop: "8px" }}>
          Try selecting a different date range
        </small>
      </div>
    );
  }

  const stats = [
    {
      label: "Total Visits",
      value: data.totalVisits,
      icon: "👁️",
      description: "All page views",
    },
    {
      label: "Unique Visitors",
      value: data.uniqueVisitors,
      icon: "👤",
      description: "Distinct users",
    },
    {
      label: "Total Sessions",
      value: data.totalSessions,
      icon: "🔄",
      description: "User sessions",
    },
    {
      label: "Unique IPs",
      value: data.uniqueIps,
      icon: "🌐",
      description: "Distinct IP addresses",
    },
    {
      label: "New Visitors",
      value: data.newVisitors,
      icon: "✨",
      description: "First-time visitors",
    },
    {
      label: "Bot Visits",
      value: data.botVisits,
      icon: "🤖",
      description: "Automated traffic",
    },
    {
      label: "Avg Load Time",
      value: data.avgPageLoadTime,
      icon: "⚡",
      description: "Page performance",
      isTime: true,
    },
  ];

  // Calculate insights
  const returningVisitors = data.totalVisits - data.newVisitors;
  const humanVisits = data.totalVisits - data.botVisits;
  const avgPagesPerSession = data.totalSessions > 0 
    ? (data.totalVisits / data.totalSessions).toFixed(2) 
    : "0";
  const botPercentage = data.totalVisits > 0 
    ? ((data.botVisits / data.totalVisits) * 100).toFixed(1)
    : "0";

  // Find max value for progress bar scaling
  const maxValue = Math.max(
    data.totalVisits,
    data.uniqueVisitors,
    data.totalSessions,
    data.uniqueIps,
    data.newVisitors,
    data.botVisits
  );

  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", padding: "0" }}>
      {/* Header */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "40px 1fr 120px",
          gap: "12px",
          padding: "8px 12px",
          borderBottom: "2px solid #e2e8f0",
          fontSize: "11px",
          fontWeight: "700",
          color: "#718096",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          flexShrink: 0,
        }}
      >
        <div></div>
        <div>Metric</div>
        <div style={{ textAlign: "right" }}>Value</div>
      </div>

      {/* Stats List - Scrollable */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        {stats.map((stat, index) => {
          const color = getStatColor(index);
          const displayValue = stat.isTime 
            ? formatLoadTime(stat.value)
            : formatNumber(stat.value);
          const progressWidth = stat.isTime 
            ? 0 
            : (stat.value / maxValue) * 100;

          return (
            <div
              key={index}
              style={{
                display: "grid",
                gridTemplateColumns: "40px 1fr 120px",
                gap: "12px",
                padding: "12px",
                borderBottom: "1px solid #f7fafc",
                alignItems: "center",
                transition: "background-color 0.2s",
                cursor: "default",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f7fafc";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {/* Icon */}
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  backgroundColor: color,
                  opacity: 0.15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    fontSize: "18px",
                  }}
                >
                  {stat.icon}
                </span>
              </div>

              {/* Label and Progress Bar */}
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#2d3748",
                    marginBottom: "4px",
                  }}
                >
                  {stat.label}
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#a0aec0",
                      fontWeight: "400",
                      marginLeft: "8px",
                    }}
                  >
                    {stat.description}
                  </span>
                </div>
                {!stat.isTime && (
                  <div
                    style={{
                      width: "100%",
                      height: "6px",
                      backgroundColor: "#e2e8f0",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${progressWidth}%`,
                        height: "100%",
                        backgroundColor: color,
                        borderRadius: "3px",
                        transition: "width 0.5s ease",
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Value */}
              <div
                style={{
                  textAlign: "right",
                  fontSize: "16px",
                  fontWeight: "700",
                  color: color,
                }}
              >
                {displayValue}
              </div>
            </div>
          );
        })}
      </div>

      {/* Insights Section - Fixed at bottom */}
      <div
        style={{
          marginTop: "0",
          padding: "12px",
          backgroundColor: "#f7fafc",
          borderRadius: "8px",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            fontSize: "12px",
            fontWeight: "700",
            color: "#2d3748",
            marginBottom: "8px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          📈 Quick Insights
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "12px",
              color: "#718096",
            }}
          >
            <span>Returning Visitors:</span>
            <span style={{ fontWeight: "700", color: "#2d3748" }}>
              {formatNumber(returningVisitors)}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "12px",
              color: "#718096",
            }}
          >
            <span>Human Visits:</span>
            <span style={{ fontWeight: "700", color: "#2d3748" }}>
              {formatNumber(humanVisits)}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "12px",
              color: "#718096",
            }}
          >
            <span>Avg Pages/Session:</span>
            <span style={{ fontWeight: "700", color: "#2d3748" }}>
              {avgPagesPerSession}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "12px",
              color: "#718096",
            }}
          >
            <span>Bot Traffic:</span>
            <span style={{ fontWeight: "700", color: "#2d3748" }}>
              {botPercentage}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrafficOverviewWidget;